import React, { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	AlertTriangle,
	ArrowUpRight,
	BellRing,
	Building2,
	CircleDollarSign,
	Loader2,
	TrendingUp,
	Wallet,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import useMoedaAtiva, {
	formatarMoeda,
	moedaPlataforma,
} from "../../hooks/useMoedaAtiva";

// A plataforma nem sempre cobra em real: pelo Stripe a moeda é a que o
// operador escolheu. Ver "R$" numa cobrança em euro seria mentira.
const dinheiro = (valor) => formatarMoeda(valor, moedaPlataforma());

// "2026-07" -> "jul/26": o eixo do gráfico não cabe o mês por extenso.
const MESES = [
	"jan",
	"fev",
	"mar",
	"abr",
	"mai",
	"jun",
	"jul",
	"ago",
	"set",
	"out",
	"nov",
	"dez",
];

const rotuloDoMes = (mes) => {
	const [ano, numero] = String(mes).split("-");

	return `${MESES[Number(numero) - 1]}/${ano.slice(2)}`;
};

// O backend devolve o status cru da assinatura; a tela mostra em pt-BR.
const SITUACOES = {
	active: "Em dia",
	trial: "Em teste",
	past_due: "Em atraso",
	paused: "Pausada",
	canceled: "Cancelada",
	blocked: "Bloqueada",
};

const RECURSOS = {
	users: "usuários",
	queues: "filas",
	connections: "conexões",
	contacts: "contatos",
};

const Indicador = ({ icone: Icone, rotulo, valor, ajuda, destaque }) => (
	<div className="rounded-xl border bg-card p-4">
		<div className="flex items-center gap-2 text-muted-foreground">
			<Icone className="h-4 w-4" />
			<p className="text-[11px] uppercase tracking-wide">{rotulo}</p>
		</div>
		<p
			className={cn(
				"mt-2 text-2xl font-semibold tabular-nums",
				destaque && "text-primary"
			)}
		>
			{valor}
		</p>
		{ajuda && <p className="mt-1 text-[11px] text-muted-foreground">{ajuda}</p>}
	</div>
);

const SaasFinance = () => {
	// Puxa a moeda da plataforma; os formatadores acima leem dela.
	useMoedaAtiva();

	const navigate = useNavigate();

	const [aba, setAba] = useState("visao");
	const [carregando, setCarregando] = useState(true);
	const [enviando, setEnviando] = useState("");

	const [metricas, setMetricas] = useState(null);
	const [receita, setReceita] = useState([]);
	const [carteira, setCarteira] = useState([]);
	const [oportunidades, setOportunidades] = useState([]);

	const carregar = useCallback(async () => {
		try {
			const [m, r, c, o] = await Promise.all([
				api.get("/saas/metrics"),
				api.get("/saas/revenue"),
				api.get("/saas/companies"),
				api.get("/saas/upgrade-candidates"),
			]);

			setMetricas(m.data);
			setReceita(r.data.map((i) => ({ ...i, rotulo: rotuloDoMes(i.mes) })));
			setCarteira(c.data.companies || []);
			setOportunidades(o.data || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const avisar = async (rota, acao, mensagem) => {
		setEnviando(acao);
		try {
			const { data } = await api.post(rota);
			toast.success(`${mensagem}: ${data?.enviados ?? 0} empresa(s).`);
		} catch (err) {
			toastError(err);
		}
		setEnviando("");
	};

	if (carregando) {
		return (
			<MainContainer>
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	const { financeiro = {}, empresas = {}, crescimento = {} } = metricas || {};

	const emAtraso = carteira.filter((c) => c.status === "past_due").length;

	const abas = [
		{ value: "visao", rotulo: "Visão geral" },
		{ value: "carteira", rotulo: "Carteira", contador: carteira.length },
		{
			value: "oportunidades",
			rotulo: "Oportunidades",
			contador: oportunidades.length,
		},
	];

	return (
		<MainContainer>
			<div className="border-b px-4 py-3 sm:px-6">
				<h1 className="text-lg font-semibold tracking-tight">
					Painel financeiro
				</h1>
				<p className="text-xs text-muted-foreground">
					Quanto o SaaS fatura, quem está pagando e quem pode crescer
				</p>
			</div>

			<PageTabs abas={abas} atual={aba} onChange={setAba} />

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{aba === "visao" && (
					<div className="space-y-4">
						<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
							<Indicador
								icone={CircleDollarSign}
								rotulo="Receita mensal"
								valor={dinheiro(financeiro.mrr)}
								ajuda={`${dinheiro(financeiro.arr)} por ano`}
								destaque
							/>
							<Indicador
								icone={Wallet}
								rotulo="Recebido no mês"
								valor={dinheiro(financeiro.recebidoEsteMes)}
								ajuda={`${dinheiro(financeiro.emAberto)} ainda em aberto`}
							/>
							<Indicador
								icone={Building2}
								rotulo="Empresas ativas"
								valor={empresas.active || 0}
								ajuda={`${empresas.total || 0} no total`}
							/>
							<Indicador
								icone={TrendingUp}
								rotulo="Ticket médio"
								valor={dinheiro(financeiro.ticketMedio)}
								ajuda={`${crescimento.novosEsteMes || 0} nova(s) este mês`}
							/>
						</div>

						<div className="rounded-xl border bg-card p-4">
							<p className="text-sm font-medium">Recebido por mês</p>
							<p className="mb-4 text-xs text-muted-foreground">
								Só faturas efetivamente pagas, nos últimos 12 meses
							</p>
							<div className="h-56 w-full">
								<ResponsiveContainer width="100%" height="100%">
									<AreaChart
										data={receita}
										margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
									>
										<defs>
											<linearGradient id="receita" x1="0" y1="0" x2="0" y2="1">
												<stop
													offset="0%"
													stopColor="#6366f1"
													stopOpacity={0.35}
												/>
												<stop
													offset="100%"
													stopColor="#6366f1"
													stopOpacity={0}
												/>
											</linearGradient>
										</defs>
										<CartesianGrid
											strokeDasharray="3 3"
											className="stroke-border"
											vertical={false}
										/>
										<XAxis
											dataKey="rotulo"
											tickLine={false}
											axisLine={false}
											className="text-[10px]"
											stroke="currentColor"
										/>
										<YAxis
											tickLine={false}
											axisLine={false}
											className="text-[10px]"
											stroke="currentColor"
										/>
										<Tooltip
											formatter={(v) => dinheiro(v)}
											labelClassName="text-xs"
											contentStyle={{
												borderRadius: 12,
												border: "1px solid hsl(var(--border))",
												background: "hsl(var(--card))",
												fontSize: 12,
											}}
										/>
										<Area
											type="monotone"
											dataKey="total"
											stroke="#6366f1"
											strokeWidth={2}
											fill="url(#receita)"
										/>
									</AreaChart>
								</ResponsiveContainer>
							</div>
						</div>

						<div className="rounded-xl border bg-card p-4">
							<p className="text-sm font-medium">Cobrança</p>
							<p className="mb-3 text-xs text-muted-foreground">
								Dispara a mensagem de cobrança no WhatsApp das empresas
							</p>
							<div className="flex flex-wrap gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={!!enviando}
									onClick={() =>
										avisar(
											"/saas/notify/due-dates",
											"vencimento",
											"Aviso de vencimento enviado"
										)
									}
								>
									{enviando === "vencimento" ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<BellRing className="h-4 w-4" />
									)}
									Avisar quem vence em breve
								</Button>
								<Button
									variant="outline"
									size="sm"
									disabled={!!enviando}
									onClick={() =>
										avisar(
											"/saas/notify/overdue",
											"atraso",
											"Cobrança de atraso enviada"
										)
									}
								>
									{enviando === "atraso" ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<AlertTriangle className="h-4 w-4" />
									)}
									Cobrar quem está em atraso
								</Button>
							</div>
						</div>
					</div>
				)}

				{aba === "carteira" && (
					<div className="space-y-2">
						{emAtraso > 0 && (
							<p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
								{emAtraso} empresa(s) com pagamento em atraso.
							</p>
						)}

						{carteira.length === 0 ? (
							<p className="py-16 text-center text-sm text-muted-foreground">
								Nenhuma empresa cadastrada ainda.
							</p>
						) : (
							carteira.map((empresa) => (
								<button
									key={empresa.id}
									type="button"
									className="flex w-full flex-wrap items-center gap-3 rounded-xl border bg-card p-3 text-left hover:bg-muted/40"
									onClick={() =>
										navigate(`/companies/${empresa.id}/subscription`)
									}
								>
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-medium">
											{empresa.name}
										</p>
										<p className="truncate text-xs text-muted-foreground">
											{empresa.plan?.name || "Sem plano"}
											{empresa.reseller && ` · ${empresa.reseller.name}`}
										</p>
									</div>

									<div className="flex shrink-0 items-center gap-3">
										<span
											className={cn(
												"rounded-full px-2 py-0.5 text-[11px] font-medium",
												empresa.status === "active" &&
													"bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
												empresa.status === "past_due" &&
													"bg-amber-500/10 text-amber-600 dark:text-amber-400",
												empresa.status === "trial" &&
													"bg-sky-500/10 text-sky-600 dark:text-sky-400",
												!["active", "past_due", "trial"].includes(
													empresa.status
												) && "bg-muted text-muted-foreground"
											)}
										>
											{SITUACOES[empresa.status] || empresa.status}
										</span>
										<span className="text-sm font-semibold tabular-nums">
											{dinheiro(empresa.total)}
										</span>
									</div>
								</button>
							))
						)}
					</div>
				)}

				{aba === "oportunidades" && (
					<div className="space-y-2">
						<p className="mb-2 text-xs text-muted-foreground">
							Empresas que já encostaram no limite do plano — é a hora de
							oferecer o upgrade.
						</p>

						{oportunidades.length === 0 ? (
							<p className="py-16 text-center text-sm text-muted-foreground">
								Ninguém perto do limite agora.
							</p>
						) : (
							oportunidades.map((item) => (
								<div
									key={item.companyId}
									className="rounded-xl border bg-card p-3"
								>
									<div className="flex flex-wrap items-center gap-2">
										<p className="min-w-0 flex-1 truncate text-sm font-medium">
											{item.name}
										</p>
										<Button
											variant="outline"
											size="sm"
											className="h-8 shrink-0"
											onClick={() =>
												navigate(
													`/companies/${item.companyId}/subscription`
												)
											}
										>
											<ArrowUpRight className="h-4 w-4" />
											Ajustar plano
										</Button>
									</div>

									<div className="mt-2 space-y-1.5">
										{item.recursos.map((recurso) => (
											<div key={recurso.resource}>
												<div className="flex justify-between text-[11px] text-muted-foreground">
													<span>
														{RECURSOS[recurso.resource] || recurso.resource}
													</span>
													<span className="tabular-nums">
														{recurso.used}/{recurso.limit}
													</span>
												</div>
												<div className="mt-0.5 h-1.5 rounded-full bg-muted">
													<div
														className={cn(
															"h-full rounded-full",
															recurso.exceeded
																? "bg-destructive"
																: "bg-amber-500"
														)}
														style={{
															width: `${Math.min(100, recurso.percent)}%`,
														}}
													/>
												</div>
											</div>
										))}
									</div>
								</div>
							))
						)}
					</div>
				)}
			</div>
		</MainContainer>
	);
};

export default SaasFinance;
